import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAudits, getAudit } from '../utils/storage';
import { PILLARS, calculateNextIndex, getScoreColor, getTier } from '../utils/scoring';
import { formatDate, exportReportToPDF } from '../utils/export';
import ClientView from '../components/ClientView';
import { getBranding } from '../components/WhiteLabelSettings';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip
} from 'recharts';

const LOCATION_COLORS = ['#C9A668', '#f87171', '#60a5fa', '#c084fc', '#34d399', '#fb923c', '#fbbf24', '#06b6d4'];

export default function ClientPortfolio() {
  const { companyId } = useParams();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [fullAudit, setFullAudit] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    getAudits().then(data => {
      // Filter audits by company tag or show all if no companyId
      const filtered = companyId
        ? data.filter(a => (a.intake?.companyTag || '').toLowerCase() === companyId.toLowerCase() ||
            (a.intake?.conceptName || '').toLowerCase().includes(companyId.toLowerCase()))
        : data;
      setAudits(filtered);
      setLoading(false);
    });
  }, [companyId]);

  // Load full audit data when selected — must be before any early returns
  useEffect(() => {
    if (selectedAudit) {
      getAudit(selectedAudit).then(data => setFullAudit(data));
    } else {
      setFullAudit(null);
    }
  }, [selectedAudit]);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-brand-gold animate-pulse font-display text-xl">Loading portfolio...</div>
    </div>
  );

  if (audits.length === 0) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <p className="text-neutral-400">No audits found for this portfolio.</p>
    </div>
  );

  // If viewing a specific audit's report
  if (selectedAudit) {
    if (!fullAudit) return <div className="min-h-screen bg-black flex items-center justify-center"><p className="text-neutral-500">Loading report...</p></div>;
    return (
      <div className="min-h-screen bg-black">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={() => setSelectedAudit(null)} className="text-xs text-neutral-400 hover:text-white mb-4">
            ← Back to Portfolio
          </button>
        </div>
        <ClientView audit={fullAudit} />
      </div>
    );
  }

  // Get branding from first audit (company-level)
  const brand = getBranding(audits[0]);

  // Portfolio overview
  const allScores = audits.map(a => ({ ...a, index: calculateNextIndex(a.scores || {}) }));
  const sorted = [...allScores].sort((a, b) => b.index - a.index);
  const avgScore = allScores.reduce((s, a) => s + a.index, 0) / allScores.length;

  // Radar comparison data
  const radarData = PILLARS.map(p => {
    const entry = { pillar: p.name.length > 12 ? p.name.substring(0, 11) + '...' : p.name };
    audits.forEach((a, i) => {
      entry[a.intake?.conceptName || `Location ${i + 1}`] = a.scores?.[p.id]?.score || 0;
    });
    return entry;
  });

  // Best/worst per pillar
  const pillarStats = PILLARS.map(p => {
    const scores = audits.map(a => ({ name: a.intake?.conceptName, score: a.scores?.[p.id]?.score || 0 })).filter(s => s.score > 0);
    const best = [...scores].sort((a, b) => b.score - a.score)[0];
    const worst = [...scores].sort((a, b) => a.score - b.score)[0];
    const avg = scores.length > 0 ? scores.reduce((s, v) => s + v.score, 0) / scores.length : 0;
    return { pillar: p.name, best, worst, avg };
  });

  const handleExport = async () => {
    setExporting(true);
    try { await exportReportToPDF('portfolio-report', 'WhatsNEXT-Portfolio.pdf'); }
    catch (e) { console.error(e); }
    setExporting(false);
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Fixed header */}
      <div className="fixed top-4 right-4 z-40">
        <button onClick={handleExport} disabled={exporting} className="btn-primary text-xs py-2 disabled:opacity-50">
          {exporting ? 'Exporting...' : 'Export PDF'}
        </button>
      </div>

      <div id="portfolio-report">
        {/* Hero */}
        <div className="bg-gradient-to-br from-black via-neutral-900 to-neutral-800 px-6 py-12 sm:px-12 sm:py-16 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img src={brand.logo} alt={brand.company || brand.name} className="h-10 w-auto max-w-[160px] object-contain" />
            {brand.company && <span className="font-bold text-white text-sm">{brand.company}</span>}
          </div>
          <h1 className="font-display text-3xl sm:text-5xl text-white mb-2">Portfolio Overview</h1>
          <p className="text-neutral-400 text-sm mb-8">{audits.length} location{audits.length !== 1 ? 's' : ''} — Business Health Dashboard</p>

          {/* Portfolio score */}
          <div className="inline-flex items-center gap-6">
            <div>
              <p className="text-5xl font-black text-brand-gold">{avgScore.toFixed(1)}</p>
              <p className="text-xs text-neutral-500 mt-1">Portfolio Average</p>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-10">

          {/* Location Rankings */}
          <h2 className="font-display text-xl text-white mb-4">Location Rankings</h2>
          <div className="space-y-2 mb-10">
            {sorted.map((a, i) => {
              const color = getScoreColor(a.index);
              const tier = getTier(a.index);
              return (
                <button
                  key={a.id}
                  onClick={() => setSelectedAudit(a.id)}
                  className="card p-4 flex items-center gap-4 w-full text-left hover:border-brand-gold/30 transition-all"
                >
                  <span className="text-lg font-black text-neutral-600 w-8">{i + 1}</span>
                  <div className={`w-12 h-12 rounded-lg ${color.bg} flex items-center justify-center flex-shrink-0`}>
                    <span className={`text-lg font-black ${color.text}`}>{a.index.toFixed(1)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white truncate">{a.intake?.conceptName}</p>
                    <p className="text-xs text-neutral-500">{a.intake?.city} — {formatDate(a.createdAt)}</p>
                  </div>
                  <span className="text-xs px-2 py-1 rounded font-medium" style={{ backgroundColor: tier.color + '20', color: tier.color }}>
                    {tier.name}
                  </span>
                  <span className="text-xs text-neutral-500">View Report →</span>
                </button>
              );
            })}
          </div>

          {/* Radar Comparison */}
          {audits.length > 1 && (
            <div className="card p-6 mb-10">
              <h2 className="font-display text-lg text-white mb-4">Cross-Location Comparison</h2>
              <div className="flex flex-wrap gap-3 mb-4">
                {audits.map((a, i) => (
                  <span key={a.id} className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: LOCATION_COLORS[i % LOCATION_COLORS.length] }}>
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: LOCATION_COLORS[i % LOCATION_COLORS.length] }} />
                    {a.intake?.conceptName}
                  </span>
                ))}
              </div>
              <div style={{ height: 400 }}>
                <ResponsiveContainer>
                  <RadarChart cx="50%" cy="50%" outerRadius="75%" data={radarData}>
                    <PolarGrid stroke="rgba(255,255,255,0.1)" />
                    <PolarAngleAxis dataKey="pillar" tick={{ fill: '#a3a3a3', fontSize: 10 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#525252', fontSize: 9 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#171717', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                    {audits.map((a, i) => (
                      <Radar
                        key={a.id}
                        name={a.intake?.conceptName || `Location ${i + 1}`}
                        dataKey={a.intake?.conceptName || `Location ${i + 1}`}
                        stroke={LOCATION_COLORS[i % LOCATION_COLORS.length]}
                        fill={LOCATION_COLORS[i % LOCATION_COLORS.length]}
                        fillOpacity={0.1}
                        strokeWidth={2}
                      />
                    ))}
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Pillar Comparison Table */}
          {audits.length > 1 && (
            <div className="card p-6 mb-10 overflow-x-auto">
              <h2 className="font-display text-lg text-white mb-4">Pillar-by-Pillar Breakdown</h2>
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left text-neutral-500 pb-3 pr-3">Pillar</th>
                    <th className="text-center text-neutral-500 pb-3 px-2">Avg</th>
                    {audits.map((a, i) => (
                      <th key={a.id} className="text-center pb-3 px-2" style={{ color: LOCATION_COLORS[i % LOCATION_COLORS.length] }}>
                        {(a.intake?.conceptName || '').substring(0, 12)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PILLARS.map(p => (
                    <tr key={p.id} className="border-t border-neutral-800/50">
                      <td className="text-neutral-300 py-2 pr-3">{p.name}</td>
                      <td className="text-center py-2 px-2 text-neutral-500">
                        {pillarStats.find(s => s.pillar === p.name)?.avg.toFixed(1) || '—'}
                      </td>
                      {audits.map(a => {
                        const s = a.scores?.[p.id]?.score || 0;
                        const color = getScoreColor(s);
                        return (
                          <td key={a.id} className={`text-center py-2 px-2 font-bold ${color.text}`}>
                            {s > 0 ? s.toFixed(1) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="border-t-2 border-neutral-700">
                    <td className="text-white font-bold py-2 pr-3">Next Index</td>
                    <td className="text-center py-2 px-2 font-bold text-brand-gold">{avgScore.toFixed(1)}</td>
                    {allScores.map(a => {
                      const color = getScoreColor(a.index);
                      return (
                        <td key={a.id} className={`text-center py-2 px-2 font-black text-lg ${color.text}`}>
                          {a.index.toFixed(1)}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Performance Highlights */}
          {audits.length > 1 && (
            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-emerald-400 mb-3">Strongest Pillars (Portfolio)</h3>
                {[...pillarStats].sort((a, b) => b.avg - a.avg).slice(0, 3).map(p => (
                  <div key={p.pillar} className="flex items-center justify-between mb-2">
                    <span className="text-xs text-neutral-300">{p.pillar}</span>
                    <span className="text-xs font-bold text-emerald-400">{p.avg.toFixed(1)}</span>
                  </div>
                ))}
              </div>
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-red-400 mb-3">Focus Areas (Portfolio)</h3>
                {[...pillarStats].sort((a, b) => a.avg - b.avg).slice(0, 3).map(p => (
                  <div key={p.pillar} className="flex items-center justify-between mb-2">
                    <span className="text-xs text-neutral-300">{p.pillar}</span>
                    <span className="text-xs font-bold text-red-400">{p.avg.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center pt-6 border-t border-neutral-800">
            {brand.showPoweredBy && (
              <div className="flex items-center justify-center gap-2 mb-2">
                <img src="https://static.wixstatic.com/media/cf458d_f7329a8f179a4571a1951c4ed9431604~mv2.png" alt="Next Experience Group" className="h-5 w-auto opacity-60" />
                <span className="text-[10px] text-neutral-600">Powered by Next Experience Group</span>
              </div>
            )}
            <div className="flex items-center justify-center gap-2 mb-2">
              <img src={brand.logo} alt={brand.company || brand.name} className="h-6 w-auto" />
              <span className="text-sm font-bold text-white">{brand.company || 'What\'s NEXT'}</span>
            </div>
            <p className="text-xs text-neutral-500">{brand.company || 'Next Experience Group'} — Portfolio Dashboard</p>
          </div>
        </div>
      </div>
    </div>
  );
}
